/**
 * cache.js - Request response caching
 *
 * Provides in-memory caching for fetch responses with configurable TTL.
 * Supports time-based expiration, SWR, persistent storage, and manual invalidation.
 */

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
const SESSION_PREFIX = 'htmlstar:cache:';

/**
 * Parse duration string to milliseconds
 * Supports: 30s, 5m, 1h, 1d
 */
export function parseDuration(duration) {
  if (duration === null || duration === undefined) return 0;
  if (duration === false || duration === 'false') return 0;
  if (duration === true) return DEFAULT_TTL;
  if (typeof duration === 'number' && Number.isFinite(duration)) return duration;

  const str = String(duration).trim();
  if (str === '' || str === 'true') return DEFAULT_TTL;

  const match = str.match(/^(\d+)(s|m|h|d)?$/);
  if (!match) return 0;

  const value = parseInt(match[1], 10);
  const unit = match[2] || 's';

  switch (unit) {
    case 's': return value * 1000;
    case 'm': return value * 60 * 1000;
    case 'h': return value * 60 * 60 * 1000;
    case 'd': return value * 24 * 60 * 60 * 1000;
    default: return value * 1000;
  }
}

/**
 * Parse cache config string. Handles plain durations and SWR syntax.
 *
 * @param {string} config - e.g. "5m", "swr:5m", "true", ""
 * @returns {{ ttl: number, swr: boolean }}
 */
export function parseCacheConfig(config) {
  if (config === null || config === undefined) return { ttl: 0, swr: false };

  const str = String(config).trim();

  if (str.startsWith('swr:')) {
    const duration = str.slice(4);
    return { ttl: parseDuration(duration || 'true'), swr: true };
  }
  if (str === 'swr') {
    return { ttl: DEFAULT_TTL, swr: true };
  }

  return { ttl: parseDuration(config), swr: false };
}

// In-memory cache
const cache = new Map();

function getCacheKey(url, method = 'GET') {
  return `${method}:${url}`;
}

/**
 * Get cached response if valid.
 * For SWR mode, returns stale entries too (caller checks isStale).
 *
 * @param {string} url
 * @param {string} method
 * @param {Object} options
 * @param {boolean} options.allowStale - Return expired entries for SWR
 * @param {string} options.storage - 'memory' or 'session'
 * @returns {{ html: string, isStale: boolean }|null}
 */
export function getFromCache(url, method = 'GET', options = {}) {
  const { allowStale = false, storage = 'memory' } = options;
  const key = getCacheKey(url, method);

  // Try memory first
  let entry = cache.get(key);

  // Fall back to sessionStorage if configured
  if (!entry && storage === 'session') {
    entry = readSessionEntry(key);
    if (entry) cache.set(key, entry); // promote to memory
  }

  if (!entry) return null;

  const expired = Date.now() > entry.timestamp + entry.ttl;

  if (expired && !allowStale) {
    cache.delete(key);
    removeSessionEntry(key);
    return null;
  }

  return { html: entry.html, isStale: expired };
}

/**
 * Store response in cache
 */
export function setInCache(url, method, html, ttl, storage = 'memory') {
  if (ttl <= 0) return;

  const key = getCacheKey(url, method);
  const entry = { html, timestamp: Date.now(), ttl };

  cache.set(key, entry);

  if (storage === 'session') {
    writeSessionEntry(key, entry);
  }
}

/** Invalidate a specific cache entry */
export function invalidateCache(url, method = 'GET') {
  const key = getCacheKey(url, method);
  cache.delete(key);
  removeSessionEntry(key);
}

/** Invalidate all cache entries matching a URL pattern */
export function invalidateCacheByPattern(pattern) {
  const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;

  for (const key of cache.keys()) {
    const url = key.replace(/^[A-Z]+:/, '');
    if (regex.test(url)) {
      cache.delete(key);
      removeSessionEntry(key);
    }
  }

  // Also scan sessionStorage for entries not in memory
  if (typeof sessionStorage !== 'undefined') {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const sKey = sessionStorage.key(i);
      if (sKey?.startsWith(SESSION_PREFIX)) {
        const cacheKey = sKey.slice(SESSION_PREFIX.length);
        const url = cacheKey.replace(/^[A-Z]+:/, '');
        if (regex.test(url)) {
          sessionStorage.removeItem(sKey);
        }
      }
    }
  }
}

/** Clear entire cache */
export function clearCache() {
  cache.clear();
  if (typeof sessionStorage !== 'undefined') {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(SESSION_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    }
  }
}

/** Get cache statistics */
export function getCacheStats() {
  return {
    size: cache.size,
    keys: Array.from(cache.keys())
  };
}

// --- sessionStorage helpers ---

function writeSessionEntry(key, entry) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(SESSION_PREFIX + key, JSON.stringify(entry));
  } catch { /* quota exceeded — silently fail */ }
}

function readSessionEntry(key) {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(SESSION_PREFIX + key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function removeSessionEntry(key) {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(SESSION_PREFIX + key);
}
